import React, { useMemo } from 'react'
import {
  AppShell,
  UnstyledButton,
  ThemeIcon,
  ScrollArea,
  Tooltip,
} from '@mantine/core'
import {
  IconIdBadge2,
  IconBuilding,
  IconBuildings,
  IconUsers,
  IconAdjustmentsHorizontal,
  IconChartHistogram,
  IconLogs,
  IconRadar2,
  IconRobot,
  IconFile,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react'
import { Link } from 'gatsby'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@gatsbyjs/reach-router'
import { useOrganizationPermissions, useUser } from '../hooks/user'
import { useOrganizationView } from './organizationView/useOrganizationView'
import {
  useIsAssessorOrganization,
  useIsHeartEyeOrganization,
  useIsTopLevelOrganization,
} from '../hooks/organizations'
import { useDevices } from '../hooks/useDevices'
import { usePipelines } from '../hooks/usePipelines'
import { featureFlags } from '../utils/featureFlags'
import * as classes from './SideMenu.module.css'

interface SideMenuProps {
  collapsed: boolean
  onToggleCollapsed: () => void
}

const SideMenu = ({ collapsed, onToggleCollapsed }: SideMenuProps) => {
  const { t } = useTranslation()

  const { data: user } = useUser(true)
  const { data: devices } = useDevices()
  // Called "pipelines" on krane-infer - shown as "Models" here, per krane-infer's src/api.rs.
  // Same visibility rule as the Devices item below: an empty (or 403'd, which the backend
  // resolves to the same empty list) response just hides the nav item rather than showing an
  // empty page. With featureFlags.models off nothing is fetched, so the item never shows.
  const { data: models } = usePipelines({ enabled: featureFlags.models })

  const { orgData } = useOrganizationView()
  const isHearteyeTenant = useIsHeartEyeOrganization()

  const isTopLevelOrganization = useIsTopLevelOrganization()
  const isEcgRecordingOrgs = useMemo(() => {
    return (
      !!(
        orgData?.assessorOrgIds &&
        Array.isArray(orgData?.assessorOrgIds) &&
        orgData?.assessorOrgIds.length > 0
      ) && isHearteyeTenant
    )
  }, [orgData?.assessorOrgIds])

  const { isAssessor } = useIsAssessorOrganization()
  const { data: permissions } = useOrganizationPermissions()

  const canViewAuditLogs =
    permissions?.find(
      (permission) => permission.relation === `audit_logs_viewer`,
    )?.allowed || false

  const canViewSubOrgs =
    permissions?.find(
      (permission) => permission.relation === `can_view_sub_orgs`,
    )?.allowed || false

  const location = useLocation()

  const ICON_SIZE = 18

  const selectedMenuItems = useMemo(
    () =>
      [
        {
          label: t(`annotations.title`),
          path: `/annotations`,
          icon: <IconFile size={ICON_SIZE} />,
        },
        devices?.items?.length > 0 && {
          label: t(`devices.title`),
          path: `/devices`,
          icon: <IconRadar2 size={ICON_SIZE} />,
        },
        (models?.length ?? 0) > 0 && {
          label: t(`models.title`),
          path: `/models`,
          icon: <IconRobot size={ICON_SIZE} />,
        },
        canViewAuditLogs && {
          label: t(`auditLogs`),
          path: `/audit`,
          icon: <IconLogs size={ICON_SIZE} />,
        },
        {
          label: t(`menu.organization`),
          path: `/organization`,
          icon: <IconBuilding size={ICON_SIZE} />,
        },
        isTopLevelOrganization &&
          (user?.roles?.includes(`ProductAdmin`) ||
            user?.roles?.includes(`SuperAdmin`) ||
            canViewSubOrgs) && {
            label: t(`menu.organizations`),
            path: `/clients`,
            icon: <IconBuildings size={ICON_SIZE} />,
          },
        !isAssessor && {
          label: t(`menu.usage`),
          path: `/usage`,
          icon: <IconChartHistogram size={ICON_SIZE} />,
        },
        !isHearteyeTenant && {
          label: t(`menu.projects`),
          path: `/project`,
          icon: <IconAdjustmentsHorizontal size={ICON_SIZE} />,
        },
        {
          label: t(`menu.members`),
          path: `/members`,
          icon: <IconIdBadge2 size={ICON_SIZE} />,
        },
        (!isAssessor || isTopLevelOrganization) && {
          label: t(`menu.patients`),
          path: `/patients`,
          icon: <IconUsers size={ICON_SIZE} />,
        },
        (isTopLevelOrganization || isEcgRecordingOrgs) &&
          isHearteyeTenant && {
            label: t(`menu.ecgs`),
            path: `/experiments`,
            icon: <IconBuilding size={ICON_SIZE} />,
          },
        isAssessor &&
          !isTopLevelOrganization && {
            label: t(`menu.ecgAssessments`),
            path: `/assessments`,
            icon: <IconBuilding size={ICON_SIZE} />,
          },
      ]
        .filter(Boolean)
        .reverse(),
    [
      isHearteyeTenant,
      isTopLevelOrganization,
      user?.roles,
      isEcgRecordingOrgs,
      isAssessor,
      t,
      canViewAuditLogs,
      devices,
      models,
    ],
  )

  return (
    <AppShell.Navbar>
      <AppShell.Section
        grow
        component={ScrollArea}
        scrollbars="y"
        className={classes.links}
      >
        {selectedMenuItems.map(({ label, path, icon }, index) => {
          const isActive = location.pathname.includes(path)
          return (
            <Tooltip
              key={index}
              label={label}
              position="right"
              disabled={!collapsed}
            >
              <UnstyledButton
                component={Link as any}
                to={path}
                className={classes.link}
                data-active={isActive || undefined}
                aria-current={isActive ? `page` : undefined}
                aria-label={collapsed ? label : undefined}
              >
                <ThemeIcon variant={isActive ? `filled` : `light`} size={30}>
                  {icon}
                </ThemeIcon>
                {!collapsed && label}
              </UnstyledButton>
            </Tooltip>
          )
        })}
      </AppShell.Section>
      <AppShell.Section className={classes.footer}>
        <Tooltip
          label={t(`menu.expand`)}
          position="right"
          disabled={!collapsed}
        >
          <UnstyledButton
            className={classes.link}
            onClick={onToggleCollapsed}
            aria-label={collapsed ? t(`menu.expand`) : undefined}
            aria-expanded={!collapsed}
          >
            <ThemeIcon variant="light" color="gray" size={30}>
              {collapsed ? (
                <IconLayoutSidebarLeftExpand size={ICON_SIZE} />
              ) : (
                <IconLayoutSidebarLeftCollapse size={ICON_SIZE} />
              )}
            </ThemeIcon>
            {!collapsed && t(`menu.collapse`)}
          </UnstyledButton>
        </Tooltip>
      </AppShell.Section>
    </AppShell.Navbar>
  )
}

export default SideMenu
